import { Autocomplete, Box, Button, Dialog, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem, Radio, RadioProps, Select, SelectChangeEvent, TextField, Typography, styled } from '@mui/material';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from "../../utils/api.types";
import { createItemCategory, updateItemCategory, createItemSubCategory, updateItemSubCategory } from '../../store/inventoryStore/inventoryThunk';

const style = {
  position: 'fixed' as 'fixed',
  zIndex: 1300,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 500,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

interface CategoryModalProps {
  open: boolean;
  editingCategory?: any;
  editingSubCategory?: any;
  selectedCategoryId?: string;
  categories?: any[];
  isSubCategory?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CategoryModal: FC<CategoryModalProps> = ({
  onClose,
  open,
  editingCategory,
  editingSubCategory,
  selectedCategoryId,
  categories = [],
  isSubCategory = false,
  onSuccess
}) => {
  const dispatch = useAppDispatch();
  const { categoriesStatus, subCategoriesStatus } = useAppSelector(state => state.inventories);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formRef = useRef<any>();

  // Schema to validate category/sub-category form
  const schema = yup.object().shape({
    name: yup.string().required(isSubCategory ? "Sub-category name is required." : "Category name is required."),
    alias: yup.string(),
    category: isSubCategory ? yup.string().required("Please select a category.") : yup.string(),
  });

  const methods = useForm<any>({ mode: 'onTouched', resolver: yupResolver(schema) });

  const onSubmit = async (data: any) => {
    setFormSubmitted(true);
    try {
      if (isSubCategory) {
        if (editingSubCategory) {
          // Update existing sub-category
          await dispatch(updateItemSubCategory({
            id: editingSubCategory._id,
            subCategoryPayload: {
              name: data.name,
              alias: data.alias || '',
              category: data.category
            }
          })).unwrap();
        } else {
          // Create new sub-category
          await dispatch(createItemSubCategory({
            name: data.name,
            alias: data.alias || '',
            category: data.category || selectedCategoryId
          })).unwrap();
        }
      } else {
        if (editingCategory) {
          // Update existing category
          await dispatch(updateItemCategory({
            id: editingCategory._id || editingCategory.value,
            categoryPayload: {
              name: data.name,
              alias: data.alias || ''
            }
          })).unwrap();
        } else {
          // Create new category
          await dispatch(createItemCategory({
            name: data.name,
            alias: data.alias || ''
          })).unwrap();
        }
      }
      onSuccess();
      onClose();
      methods.reset();
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setFormSubmitted(false);
    }
  };

  useEffect(() => {
    if (isSubCategory) {
      if (editingSubCategory) {
        methods.setValue('name', editingSubCategory.name);
        methods.setValue('alias', editingSubCategory.alias || '');
        methods.setValue('category', editingSubCategory.category);
      } else {
        methods.setValue('category', selectedCategoryId || '');
        methods.reset({ category: selectedCategoryId || '' });
      }
    } else {
      if (editingCategory) {
        methods.setValue('name', editingCategory.label || editingCategory.name);
        methods.setValue('alias', editingCategory.alias || '');
      } else {
        methods.reset();
      }
    }
  }, [editingCategory, editingSubCategory, isSubCategory, selectedCategoryId, methods]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ backgroundColor: '#e9ecf1', padding: '8px 24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            {isSubCategory
              ? (editingSubCategory ? 'Update Sub-Category' : 'Add New Sub-Category')
              : (editingCategory ? 'Update Category' : 'Add New Category')}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              type="button"
              name={'submit'}
              loading={(isSubCategory ? subCategoriesStatus : categoriesStatus) === ApiStatus.LOADING && formSubmitted}
              style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff' }}
              variant="contained"
              size='small'
              onClick={() => {
                if (formRef.current) {
                  formRef.current.requestSubmit();
                }
              }}
            >
              {(isSubCategory ? editingSubCategory : editingCategory) ? 'Update' : 'Save'}
            </LoadingButton>
            <Button
              style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff' }}
              variant="contained"
              size='small'
              onClick={() => {
                methods.reset();
                onClose();
              }} >
              Cancel
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(data => onSubmit(data))} ref={formRef} noValidate>
            <Grid container spacing={2} alignItems="center" columnSpacing={2}>
              {isSubCategory && (
                <Grid item xs={12}>
                  <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14, marginBottom: 4 }}>Select Category *</h5>
                  <Select
                    {...methods.register('category')}
                    value={methods.watch('category') || ''}
                    onChange={(e) => methods.setValue('category', e.target.value)}
                    size="small"
                    className='c-text-field-4'
                    // variant="standard"
                    sx={{ width: '100%', mt: 0.5 }}
                  >
                    {categories.map(cat => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <p style={{ color: "#c33b3b" }}>{methods.formState.errors.category?.message ?? ""}</p>
                </Grid>
              )}
              <Grid item xs={6}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14, marginBottom: 4 }}>
                  {isSubCategory ? 'Sub-Category Name *' : 'Category Name *'}
                </h5>
                <TextField
                  placeholder={isSubCategory ? "Enter Sub-Category Name" : "Enter Category Name"}
                  required
                  size="small"
                  {...methods.register('name')}
                  name="name"
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
                <p style={{ color: "#c33b3b" }}>{methods.formState.errors.name?.message ?? ""}</p>
              </Grid>
              <Grid item xs={6}>
                <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14, marginBottom: 4 }}>Alias (Optional)</h5>
                <TextField
                  placeholder="Enter Alias"
                  size="small"
                  {...methods.register('alias')}
                  name="alias"
                  className='c-text-field-4'
                  variant="standard"
                  sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryModal;
